import React, { useEffect, useRef } from 'react';
import '../styles/About.scss';
import profileImage from '../images/profile.jpeg';
import awsCertLogo from '../images/aws-solutions-architect-cert.png';

const About = ({ setPage }) => {
  const aboutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <section className="about-section" ref={aboutRef}>
      <h2 className="animate-on-scroll">About Me</h2>
      
      <div className="professional-summary animate-on-scroll">
        <h3>Professional Summary</h3>
        <p>
          A results-driven Software Engineer with <strong>7+ years of experience</strong> in full-stack development across banking, finance, and enterprise applications. 
          Specialized in AWS cloud solutions, microservices architecture, and modern frontend frameworks. 
          Proven track record of building scalable applications and leading development teams to deliver high-quality software solutions.
        </p>
      </div>
      
      <div className="about-content">
        <div className="about-content__text animate-on-scroll">
          <p>
            Hello! I'm <strong>LE THINH PHAT</strong>, a results-driven Software Engineer with extensive experience in full-stack development.
            I specialize in crafting innovative, high-performance solutions to complex technical challenges.
          </p>
          <p>
            My approach to software development combines strong technical expertise with a passion for clean code and efficient systems.
            I thrive in collaborative environments where I can contribute to meaningful projects that make a difference.
          </p>
          <p>
            With expertise across various technologies including Java/Spring Boot, AWS cloud services, and modern web frameworks,
            I enjoy building robust applications that scale well and deliver exceptional user experiences.
          </p>
        </div>
        
        <div className="about-content__image animate-on-scroll">
          <img src={profileImage} alt="LE THINH PHAT working on code" />
        </div>
      </div>
      
      <div className="education animate-on-scroll">
        <h3>Education</h3>
        
        <div className="education__timeline">
          <div className="education__item">
            <div className="degree">Master of Science in Data Science</div>
            <div className="school">Liverpool John Moores University</div>
            <div className="date">Expected Graduation: December 2025</div>
          </div>
          
          <div className="education__item">
            <div className="degree">Bachelor of Software Engineering</div>
            <div className="school">FPT University</div>
            <div className="date">2013 - 2018</div>
            <div className="gpa">GPA: 7/10</div>
          </div>
        </div>
      </div>
      
      <div className="certifications animate-on-scroll">
        <h3>Certifications</h3>
        <div className="certifications__container">
          <a 
            href="https://www.credly.com/badges/2d6b0208-11eb-4fcc-a58a-b03c6e5da80b/public_url" 
            target="_blank" 
            rel="noopener noreferrer"
            className="certification-card"
          >
            <div className="certification-logo">
              <img src={awsCertLogo} alt="AWS Certified Solutions Architect – Associate" />
            </div>
            <div className="certification-details">
              <h4>AWS Certified Solutions Architect – Associate</h4>
              <p>Amazon Web Services</p>
              <span className="certification-date">Verify on Credly <span className="verification-icon">↗</span></span>
            </div>
          </a>
        </div>
      </div>
      
      <div className="journey animate-on-scroll">
        <h3>Professional Experience</h3>
        
        <div className="journey__timeline">
          <div className="journey__item">
            <div className="date">July 2024 - Present</div>
            <div className="title">Senior Software Engineer at TymeX</div>
            <div className="project">Project: Personal Loan Application</div>
            <div className="description">
              Banking web application for personal loans that manages the entire process—from verification to servicing.
              <div className="tech-stack">Tech Stack: AWS, Python, SpringBoot</div>
              <ul className="responsibilities">
                <li>Design systems and workflows to enhance loan application processes</li>
                <li>Develop and implement new features based on specifications</li>
                <li>Mentor team members and ensure smooth integration of features</li>
              </ul>
            </div>
          </div>
          
          <div className="journey__item">
            <div className="date">July 2022 - July 2024</div>
            <div className="title">Software Engineer at SEVEN CIRCLES</div>
            <div className="project">Projects: Fin-ally, Prepa-inp, Reveal</div>
            <div className="description">
              Worked on multiple applications across financial, education, and data processing domains.
              <div className="tech-stack">Tech Stack: Java, SpringBoot, Guice, ReactJS, MySQL, Elasticsearch, Docker</div>
              <ul className="responsibilities">
                <li>Developed and maintained web applications using Java and ReactJS</li>
                <li>Enhanced UI and improved application performance through query optimization</li>
                <li>Managed UAT and production deployments, monitored issues using Kibana and Sentry</li>
              </ul>
            </div>
          </div>
          
          <div className="journey__item">
            <div className="date">July 2020 - July 2022</div>
            <div className="title">Back-end Developer at Asia Commercial Bank (ACB)</div>
            <div className="project">Projects: Mobile Banking, Virtual Debit Card, ePin, Sunlife Insurance</div>
            <div className="description">
              Developed banking services and applications for a major commercial bank.
              <div className="tech-stack">Tech Stack: Java SpringBoot, Postgres, Docker, Kubernetes, AWS S3</div>
              <ul className="responsibilities">
                <li>Implemented microservices architecture and migrated monolithic applications</li>
                <li>Managed development environments and collaborated with DevOps for deployments</li>
                <li>Built services from scratch and conducted code reviews to ensure standards</li>
              </ul>
            </div>
          </div>
          
          <div className="journey__item">
            <div className="date">2019 - 2020</div>
            <div className="title">Full-Stack Developer at Robert Bosch Engineering</div>
            <div className="project">Project: WorkOn</div>
            <div className="description">
              Web application for generating business case forms for Bosch's global teams.
              <div className="tech-stack">Tech Stack: JavaScript, Bosch internal framework</div>
              <ul className="responsibilities">
                <li>Maintained and developed applications using Bosch's internal platform</li>
                <li>Gathered requirements directly from international stakeholders</li>
              </ul>
            </div>
          </div>
          
          <div className="journey__item">
            <div className="date">2018 - 2019</div>
            <div className="title">Full-Stack Developer at FPT Software</div>
            <div className="project">Project: CES-Blue</div>
            <div className="description">
              Dashboard and web application for US energy companies using Spring Boot and AngularJS.
              <div className="tech-stack">Tech Stack: Spring Boot, AngularJS</div>
              <ul className="responsibilities">
                <li>Collaborated with designers to enhance UI and improve user satisfaction</li>
                <li>Demonstrated new features to clients and gathered feedback</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="interests animate-on-scroll">
        <h3>Beyond Coding</h3>
        
        <div className="interests__grid">
          <div className="interests__item">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span>Continuous Learning</span>
          </div>
          
          <div className="interests__item">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Travel</span>
          </div>
          
          <div className="interests__item">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
            <span>Tech Meetups</span>
          </div>
          
          <div className="interests__item">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span>Problem Solving</span>
          </div>
          
          <div className="interests__item">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>Data Analysis</span>
          </div>
          
          <div className="interests__item">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <span>Tech Communities</span>
          </div>
        </div>
      </div>
      
      <div className="cta-container animate-on-scroll">
        <button 
          onClick={() => setPage('Projects')} 
          className="cta-button"
        >
          View My Projects
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>
        <button 
          onClick={() => setPage('Contact')} 
          className="cta-button cta-button--secondary"
        >
          Get In Touch
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
        </button>
      </div>
    </section>
  );
};

export default About;
